import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MixingFormulationWithMaterials, WorkOrder } from '@twinsketch/topika-model';
import FormulationTable from 'src/pages/workOrder/form/FormulationTable';
import { useFetchDataById } from 'src/hook/CustomHook';
import ApiService from 'src/services/ApiService';
import {
  FormulationToMixingHelper,
  translateToFromulationToMixingHelper,
} from 'src/pages/workOrder/form/helper';
import ROUTES from 'src/constants/routes';
import ErrorModal from 'src/components/common/ErrorModal';
import DetailHeader from 'src/components/detailPage/DetailHeader';
import { handleEdit } from 'src/hook/CommonHandler';
import SlurryMixingTable from './workOrderComponent/tables/SlurryMixingTable';
import BinderMixingTable from './workOrderComponent/tables/BinderMixingTable';
import CoatingSpecTable from './workOrderComponent/tables/CoatingSpecTable';
import CalenderingSpecTable from './workOrderComponent/tables/CalenderingSpecTable';
import SlittingSpecTable from './workOrderComponent/tables/SlittingSpecTable';
import InspectionTable from './workOrderComponent/tables/InspectionTable';
import AmountYieldsTable from './workOrderComponent/tables/AmountYieldTable';
import ProcessYieldTable from './workOrderComponent/tables/ProcessYieldTable';
import TankTable from './workOrderComponent/tables/TankTable';
import { useUser } from 'src/auth/UserContext';
import FinalViscosityChart from './workOrderComponent/charts/FinalViscosityChart';
import SolidContentAccuracyChart from './workOrderComponent/charts/SolidContentAccuracyChart';
import NMPConsumptionChart from './workOrderComponent/charts/NMPConsumptionChart';
import 'src/styles/workOrder/work-order.css';
import MaterialUsageComparison from './workOrderComponent/charts/MaterialUsageComparison';

const MinimumWorkOrderForm: React.FC<{}> = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [selectedFormulation, setSelectedFormulation] =
    useState<MixingFormulationWithMaterials | null>(null);
  const [formulationHelper, setFormulationHelper] = useState<FormulationToMixingHelper | null>(
    null
  );
  const [draftMessage, setDraftMessage] = useState<string | null>(null);
  const [workOrder, setWorkOrder] = useState<WorkOrder | null>(null);
  const { platformUser: user } = useUser();
  const canWrite = user?.permissions?.includes('write');

  const sortedMixingWorks = useMemo(() => {
    if (!workOrder?.mixingWorks) return [];

    return [...workOrder.mixingWorks].sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return dateB - dateA; // Most recent first
    });
  }, [workOrder?.mixingWorks]);

  useFetchDataById(ApiService.fetchWorkOrderById, id, setWorkOrder, setError);

  useEffect(() => {
    const fetchFormulation = async () => {
      if (workOrder?.formulationId) {
        try {
          const formulation = await ApiService.fetchFormulationById(workOrder.formulationId);
          const details = translateToFromulationToMixingHelper(
            formulation,
            workOrder.activeMaterialKg
          );
          setSelectedFormulation(formulation);
          setFormulationHelper(details);
        } catch (fetchError) {
          console.error('Error fetching formulation:', fetchError);
          setError('Failed to fetch formulation data.');
        }
      }
    };

    fetchFormulation();
  }, [workOrder]);

  useEffect(() => {
    if (workOrder?.attachedFilesInfo?.length && id) {
      const hasWorkOrderFile = workOrder.attachedFilesInfo.some(
        file => file.category === 'WORK_ORDER'
      );
      if (!hasWorkOrderFile) {
        setDraftMessage(
          'This work order is draft. Please upload a valid work order file to make it ready.'
        );
      }
    }
  }, [workOrder, id]);

  const handleWorkOrderDelete = async () => {
    if (!id) {
      setError('Work order ID is missing.');
      return;
    }
    try {
      await ApiService.deleteWorkOrderById(id);
      alert('Work order deleted successfully.');
      navigate(ROUTES.WORK_ORDERS);
    } catch (deleteError) {
      console.error('Failed to delete work order:', deleteError);
      setError('Failed to delete the work order. Please try again.');
    }
  };

  const updateWorkOrderFlag = async (isFlagged: boolean) => {
    if (!id) return;
    try {
      await ApiService.updateWorkOrder(id, { ...workOrder, flagged: isFlagged });
      setWorkOrder(prevWorkOrder => ({
        ...prevWorkOrder!,
        flagged: isFlagged,
      }));
    } catch (updateError) {
      console.error('Failed to update work order flag:', updateError);
      setError('Failed to update work order flag.');
    }
  };

  const handleEditWorkOrder = () => {
    if (id) {
      handleEdit(id, navigate, setError, ROUTES.WORK_ORDERS_EDIT(id));
    }
  };

  if (!workOrder) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <div className="content-wrapper">
        <ErrorModal message={error} onClose={() => setError(null)} />
        <div>
          {draftMessage && (
            <div
              className="error-message"
              style={{ color: 'red', textAlign: 'left', marginBottom: '1rem' }}
            >
              {draftMessage}
            </div>
          )}
        </div>
        <div>
          <div className="form-group">
            <DetailHeader
              title={`${workOrder.name || ''}`}
              handleEdit={handleEditWorkOrder}
              handleDelete={handleWorkOrderDelete}
              createdAt={workOrder.createdAt}
              createdBy={workOrder.createdBy}
              updatedBy={workOrder.updatedBy}
              canWrite={canWrite}
            />
            <label>
              <input
                type="checkbox"
                checked={workOrder.flagged || false}
                onChange={e => updateWorkOrderFlag(e.target.checked)}
              />
              Flag this work order
            </label>
          </div>
        </div>

        <div className="tables-section">
          <div className="tables-row">
            {/* Left Column - Formulation & Quality */}
            <div className="table-group">
              <FormulationTable
                formulation={formulationHelper}
                setFormulation={setFormulationHelper}
                onFormulationUpdate={updatedFormulation => {
                  setFormulationHelper(updatedFormulation);
                }}
                activeMaterialKg={workOrder.activeMaterialKg}
                numberOfBatch={workOrder.numberOfBatch ?? 1}
              />
              <TankTable
                mixingTankVolumeFull={workOrder.mixingTankVolumeFull || 700}
                mixingTankVolumeWorking={workOrder.mixingTankVolumeWorking || 480}
                onUpdate={updatedValues =>
                  setWorkOrder(prevWorkOrder => ({
                    ...prevWorkOrder,
                    ...updatedValues,
                  }))
                }
              />
              <div className="section-title">Quality Measurements</div>
              <table className="quality-table-horizontal">
                <thead>
                  <tr>
                    <th>Nail Penetration Resistance (Ω)</th>
                    <th>Moisture (ppm)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{workOrder.measuredNailResistance ?? '–'}</td>
                    <td>{workOrder.measuredMoisture ?? '–'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            {/* Right Column - Process Tables */}
            <div className="table-group">
              {workOrder.instructionNotes?.trim() && (
                <>
                  <div className="section-title">Instruction Notes</div>
                  <div className="instruction-notes-text">{workOrder.instructionNotes}</div>
                </>
              )}
              <AmountYieldsTable
                materialsNeeded={workOrder.materialsNeeded}
                formulation={formulationHelper}
                onUpdate={async data => {
                  try {
                    if (!id) throw new Error('Work order ID is missing');

                    const updatedWorkOrder = {
                      ...workOrder,
                      materialsNeeded: data,
                    };

                    const response = await ApiService.updateWorkOrder(id, updatedWorkOrder);
                    setWorkOrder(response);
                  } catch (err: any) {
                    setError(err.message);
                    throw err;
                  }
                }}
                readonly={false}
              />
              <ProcessYieldTable
                processYield={workOrder.processYield}
                onUpdate={async data => {
                  try {
                    if (!id) throw new Error('Work order ID is missing');

                    const updatedWorkOrder = {
                      ...workOrder,
                      processYield: data,
                    };

                    const response = await ApiService.updateWorkOrder(id, updatedWorkOrder);
                    setWorkOrder(response);
                  } catch (err: any) {
                    setError(err.message);
                    throw err;
                  }
                }}
                readonly={false}
              />
              <InspectionTable
                inspection={workOrder.inspectionBreakdown}
                onUpdate={async data => {
                  try {
                    if (!id) throw new Error('Work order ID is missing');

                    const updatedWorkOrder = {
                      ...workOrder,
                      inspectionBreakdown: data,
                    };

                    const response = await ApiService.updateWorkOrder(id, updatedWorkOrder);
                    setWorkOrder(response);
                  } catch (err: any) {
                    setError(err.message);
                    throw err;
                  }
                }}
                readonly={false}
                electrodeType={formulationHelper?.formulation?.type}
              />
            </div>
          </div>
          {workOrder.inspectionBreakdown && workOrder.mixingWorks && (
            <div className="charts-section">
              <h3 className="section-header">Analysis Charts</h3>
              <div className="charts-grid">
                <FinalViscosityChart
                  mixingWorks={sortedMixingWorks}
                  inspectionBreakdown={workOrder.inspectionBreakdown}
                />
                <SolidContentAccuracyChart
                  mixingWorks={sortedMixingWorks}
                  inspectionBreakdown={workOrder.inspectionBreakdown}
                />
                <NMPConsumptionChart mixingWorks={sortedMixingWorks} />
              </div>
              <div>
                <MaterialUsageComparison
                  materialsNeeded={workOrder.materialsNeeded}
                  mixingWorks={workOrder.mixingWorks}
                  activeMaterialKg={workOrder.activeMaterialKg}
                  formulation={formulationHelper}
                />
              </div>
            </div>
          )}
          {/* Specifications Section */}
          <div className="specs-section">
            <h3 className="section-header">Manufacturing Specifications</h3>
            <div className="specs-grid"></div>
            <BinderMixingTable data={workOrder.binderMixing} formulation={formulationHelper} />
            <SlurryMixingTable data={workOrder.slurryMixing} formulation={formulationHelper} />
            <CoatingSpecTable coatingSpec={workOrder.coatingSpec} />
            <CalenderingSpecTable calenderingSpec={workOrder.calenderingSpec} />
            <SlittingSpecTable slittingSpec={workOrder.slittingSpec} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MinimumWorkOrderForm;
